import { Visibility, VisibilityOff } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Modal,
    OutlinedInput,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { Report } from 'notiflix';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { ROLE_IDS, isCompany } from '../../../config/roles';
import { getAllDocumentType } from '../../../data/api/document/DocumentRoutes';
import { getRoles } from '../../../data/api/roles/RoleRoutes';
import { createUser, getAllUsers } from '../../../data/api/users/UserRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        background: '#fff',
        color: '#10285f',
        fontWeight: 700,
        transition: '0.25s ease',

        '& fieldset': {
            borderColor: 'rgba(15, 34, 77, 0.16)',
        },

        '&:hover fieldset': {
            borderColor: 'rgba(214, 170, 58, 0.7)',
        },

        '&.Mui-focused fieldset': {
            borderColor: '#d6aa3a',
            borderWidth: '2px',
        },
    },

    '& .MuiInputLabel-root': {
        color: 'rgba(15, 34, 77, 0.65)',
        fontWeight: 700,
    },

    '& .MuiInputLabel-root.Mui-focused': {
        color: '#10285f',
    },

    '& .MuiSvgIcon-root': {
        color: '#10285f',
    },
};

const CreateUserModal = ({ open, onClose, onCreated }) => {
    const { auth, token } = useIndex();
    const empresa = isCompany(auth);

    const {
        register,
        handleSubmit,
        control,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            name: '',
            lastname: '',
            email: '',
            phone: '',
            document: '',
            document_type_id: '',
            role_id: empresa ? ROLE_IDS.DISTRIBUIDOR : '',
            company_id: '',
            password: '',
            password_confirmation: '',
        },
    });

    const [roles, setRoles] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const selectedRole = Number(watch('role_id'));
    const showCompanySelector = !empresa && selectedRole === ROLE_IDS.DISTRIBUIDOR;

    // Opciones de rol para el selector del admin: roles activos, garantizando
    // siempre la presencia del rol Distribuidor aunque venga inactivo en la
    // tabla `roles` (el backend solo valida que el role_id exista, no su status).
    const roleOptions = useMemo(() => {
        const active = roles.filter((r) => r.status === 1 || r.status === true);

        if (!active.some((r) => r.id === ROLE_IDS.DISTRIBUIDOR)) {
            const distribuidor = roles.find((r) => r.id === ROLE_IDS.DISTRIBUIDOR);
            active.push(distribuidor ?? { id: ROLE_IDS.DISTRIBUIDOR, name: 'Distribuidor' });
        }

        return active;
    }, [roles]);

    useEffect(() => {
        if (!open) return;

        if (!empresa) loadRoles();
        loadDocumentTypes();
    }, [open]);

    useEffect(() => {
        if (open && showCompanySelector && companies.length === 0) {
            loadCompanies();
        }
    }, [open, showCompanySelector]);

    const loadRoles = async () => {
        try {
            const { data } = await getRoles(token);
            setRoles(data?.data ?? []);
        } catch (error) {
            validErrors(error);
        }
    };

    const loadDocumentTypes = async () => {
        try {
            const { data } = await getAllDocumentType();
            setDocumentTypes(data?.data ?? []);
        } catch (error) {
            validErrors(error);
        }
    };

    const loadCompanies = async () => {
        setLoadingCompanies(true);

        try {
            const { data } = await getAllUsers(token, 100, 1, '', {
                role_id: ROLE_IDS.EMPRESA,
            });

            setCompanies(data?.data ?? []);
        } catch (error) {
            validErrors(error);
        } finally {
            setLoadingCompanies(false);
        }
    };

    const handleClose = () => {
        if (submitting) return;
        reset();
        onClose?.();
    };

    const onSubmit = async (form) => {
        const payload = {
            ...form,
            role_id: empresa ? ROLE_IDS.DISTRIBUIDOR : Number(form.role_id),
            company_id: empresa ? null : (form.company_id ? Number(form.company_id) : null),
            password_confirmation: form.password,
            status: 1,
            game_enabled: 1,
        };

        setSubmitting(true);

        try {
            const { data } = await createUser(token, payload);

            if (data?.success) {
                Report.success(
                    'Éxito',
                    data?.message ?? 'Usuario creado correctamente.',
                    'Okay',
                    { textAlign: 'center' }
                );

                reset();
                onCreated?.();
                onClose?.();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Center>
                <ModalCard>
                    <ModalHeader>
                        <Box className="title-wrap">
                            <Box className="icon-box">
                                <PersonAddAlt1OutlinedIcon />
                            </Box>

                            <Box>
                                <Typography className="title">
                                    {empresa ? 'Crear distribuidor' : 'Crear usuario'}
                                </Typography>

                                <Typography className="subtitle">
                                    Registra la información básica, documento, rol y acceso.
                                </Typography>
                            </Box>
                        </Box>

                        <IconButton
                            onClick={handleClose}
                            size="small"
                            sx={{
                                color: '#10285f',
                                background: 'rgba(16, 40, 95, 0.06)',
                                '&:hover': {
                                    background: 'rgba(214, 170, 58, 0.16)',
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </ModalHeader>

                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <SectionCard>
                            <SectionTitle>
                                <BadgeOutlinedIcon />
                                <Box>
                                    <Typography className="section-title">
                                        Información personal
                                    </Typography>
                                    <Typography className="section-subtitle">
                                        Datos básicos del usuario
                                    </Typography>
                                </Box>
                            </SectionTitle>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Nombre *"
                                        size="small"
                                        error={!!errors.name}
                                        helperText={errors.name?.message}
                                        sx={fieldStyles}
                                        {...register('name', {
                                            required: 'El nombre es obligatorio.',
                                        })}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Apellido"
                                        size="small"
                                        sx={fieldStyles}
                                        {...register('lastname')}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Correo *"
                                        size="small"
                                        type="email"
                                        error={!!errors.email}
                                        helperText={errors.email?.message}
                                        sx={fieldStyles}
                                        {...register('email', {
                                            required: 'El correo es obligatorio.',
                                        })}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Teléfono"
                                        size="small"
                                        type="number"
                                        sx={fieldStyles}
                                        {...register('phone')}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="document_type_id"
                                        control={control}
                                        rules={{
                                            required: 'Seleccione un tipo de documento.',
                                        }}
                                        render={({ field }) => (
                                            <FormControl
                                                fullWidth
                                                size="small"
                                                error={!!errors.document_type_id}
                                                sx={fieldStyles}
                                            >
                                                <InputLabel id="document_type_label">
                                                    Tipo documento *
                                                </InputLabel>

                                                <Select
                                                    {...field}
                                                    labelId="document_type_label"
                                                    label="Tipo documento *"
                                                    value={field.value ?? ''}
                                                >
                                                    {documentTypes.map((dt) => (
                                                        <MenuItem key={dt.id} value={dt.id}>
                                                            {dt.short_name ?? dt.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>

                                                {errors.document_type_id && (
                                                    <Typography className="field-error">
                                                        {errors.document_type_id.message}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={8}>
                                    <TextField
                                        fullWidth
                                        label="Documento *"
                                        size="small"
                                        error={!!errors.document}
                                        helperText={errors.document?.message}
                                        sx={fieldStyles}
                                        {...register('document', {
                                            required: 'El documento es obligatorio.',
                                        })}
                                    />
                                </Grid>
                            </Grid>
                        </SectionCard>

                        <SectionCard>
                            <SectionTitle>
                                <BusinessOutlinedIcon />
                                <Box>
                                    <Typography className="section-title">
                                        Rol y asociación
                                    </Typography>
                                    <Typography className="section-subtitle">
                                        Define los permisos y empresa asociada
                                    </Typography>
                                </Box>
                            </SectionTitle>

                            <Grid container spacing={2}>
                                {!empresa && (
                                    <Grid item xs={12} md={showCompanySelector ? 6 : 12}>
                                        <Controller
                                            name="role_id"
                                            control={control}
                                            rules={{ required: 'Seleccione un rol.' }}
                                            render={({ field }) => (
                                                <FormControl
                                                    fullWidth
                                                    size="small"
                                                    error={!!errors.role_id}
                                                    sx={fieldStyles}
                                                >
                                                    <InputLabel id="role_label">Rol *</InputLabel>

                                                    <Select
                                                        {...field}
                                                        labelId="role_label"
                                                        label="Rol *"
                                                        value={field.value ?? ''}
                                                    >
                                                        {roleOptions.map((r) => (
                                                            <MenuItem key={r.id} value={r.id}>
                                                                {r.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>

                                                    {errors.role_id && (
                                                        <Typography className="field-error">
                                                            {errors.role_id.message}
                                                        </Typography>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                )}

                                {empresa && (
                                    <Grid item xs={12}>
                                        <RoleNotice>
                                            Este usuario será creado como <strong>Distribuidor</strong>.
                                        </RoleNotice>
                                    </Grid>
                                )}

                                {showCompanySelector && (
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="company_id"
                                            control={control}
                                            render={({ field }) => (
                                                <FormControl
                                                    fullWidth
                                                    size="small"
                                                    error={!!errors.company_id}
                                                    sx={fieldStyles}
                                                >
                                                    <InputLabel id="company_label">
                                                        Empresa asociada (opcional)
                                                    </InputLabel>

                                                    <Select
                                                        {...field}
                                                        labelId="company_label"
                                                        label="Empresa asociada (opcional)"
                                                        value={field.value ?? ''}
                                                        disabled={loadingCompanies}
                                                    >
                                                        <MenuItem value="">
                                                            <em>Sin empresa (asociar al administrador)</em>
                                                        </MenuItem>

                                                        {companies.map((c) => (
                                                            <MenuItem key={c.id} value={c.id}>
                                                                {c.name} {c.lastname}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>

                                                    {errors.company_id && (
                                                        <Typography className="field-error">
                                                            {errors.company_id.message}
                                                        </Typography>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </SectionCard>

                        <SectionCard>
                            <SectionTitle>
                                <LockOutlinedIcon />
                                <Box>
                                    <Typography className="section-title">
                                        Acceso
                                    </Typography>
                                    <Typography className="section-subtitle">
                                        Define la contraseña inicial del usuario
                                    </Typography>
                                </Box>
                            </SectionTitle>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        error={!!errors.password}
                                        sx={fieldStyles}
                                    >
                                        <InputLabel htmlFor="new_password">
                                            Contraseña *
                                        </InputLabel>

                                        <OutlinedInput
                                            id="new_password"
                                            type={showPassword ? 'text' : 'password'}
                                            label="Contraseña *"
                                            {...register('password', {
                                                required: 'La contraseña es obligatoria.',
                                                minLength: {
                                                    value: 8,
                                                    message: 'Mínimo 8 caracteres.',
                                                },
                                            })}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword((s) => !s)}
                                                        edge="end"
                                                        size="small"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                        />

                                        {errors.password && (
                                            <Typography className="field-error">
                                                {errors.password.message}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </SectionCard>

                        <Actions>
                            <LoadingButton
                                fullWidth
                                type="submit"
                                variant="contained"
                                loading={submitting}
                                startIcon={<i className="fa-solid fa-user-plus"></i>}
                                sx={{
                                    borderRadius: '14px',
                                    py: 1.1,
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                    boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                    },
                                }}
                            >
                                <span>Crear usuario</span>
                            </LoadingButton>
                        </Actions>
                    </form>
                </ModalCard>
            </Center>
        </Modal>
    );
};

export default CreateUserModal;

const Center = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(92vw, 760px);
    max-height: 92vh;
    outline: none;
`;

const ModalCard = styled.div`
    width: 100%;
    max-height: 92vh;
    overflow-y: auto;
    border-radius: 22px;
    padding: 1.2rem;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border: 1px solid rgba(214, 170, 58, 0.28);
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.22);

    .field-error {
        display: block;
        margin-top: 0.35rem;
        margin-left: 0.7rem;
        color: #d32f2f;
        font-size: 0.75rem;
        font-weight: 700;
    }

    @media (max-width: 600px) {
        padding: 0.9rem;
        border-radius: 18px;
    }
`;

const ModalHeader = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1rem;

    .title-wrap {
        display: flex;
        align-items: center;
        gap: 0.8rem;
    }

    .icon-box {
        width: 44px;
        height: 44px;
        border-radius: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #10285f;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        flex-shrink: 0;
    }

    .title {
        color: #10285f;
        font-size: 1.35rem;
        font-weight: 900;
        line-height: 1.1;
    }

    .subtitle {
        margin-top: 0.15rem;
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.86rem;
        font-weight: 600;
    }

    @media (max-width: 600px) {
        .title {
            font-size: 1.1rem;
        }

        .subtitle {
            font-size: 0.78rem;
        }
    }
`;

const SectionCard = styled.div`
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 18px;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: rgba(16, 40, 95, 0.025);

    @media (max-width: 600px) {
        padding: 0.85rem;
    }
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.7rem;
    margin-bottom: 1rem;

    svg {
        color: #d6aa3a;
        font-size: 1.35rem;
        flex-shrink: 0;
    }

    .section-title {
        color: #10285f;
        font-size: 0.98rem;
        font-weight: 900;
        line-height: 1.1;
    }

    .section-subtitle {
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.78rem;
        font-weight: 600;
        margin-top: 0.1rem;
    }
`;

const RoleNotice = styled.div`
    padding: 0.75rem 1rem;
    border-radius: 14px;
    background: rgba(214, 170, 58, 0.14);
    border: 1px solid rgba(214, 170, 58, 0.28);
    color: #10285f;
    font-size: 0.9rem;
    font-weight: 700;

    strong {
        font-weight: 900;
        color: #8a6500;
    }
`;

const Actions = styled.div`
    margin-top: 0.3rem;
`;